import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { config } from '../config';
import { createCorrelationId, logEvent } from './logger';

export type AxiosQueryResponse = AxiosResponse;

// Extend AxiosRequestConfig to include metadata for timing and correlation
declare module 'axios' {
  interface AxiosRequestConfig {
    metadata?: {
      correlationId: string;
      startTime: number;
    };
  }
}

export const axios = Axios.create({
  baseURL: config.serverUrl,
  timeout: 30000
});

export const dotNetAxios = Axios.create({
  baseURL: config.dotnetServerUrl,
  timeout: 60000
});

export const axiosInstances: AxiosInstance[] = [axios, dotNetAxios];

export const setSessionAndCallId = (sessionId: string): void => {
  axiosInstances.forEach((axiosInstance) =>
    axiosInstance.interceptors.request.use(async (config) => {
      // New standard headers
      // config.headers['X-Session-ID'] = sessionId;
      // config.headers['X-Correlation-ID'] = config.metadata?.correlationId || createCorrelationId();

      // Keep old headers during transition for Lambda backends
      config.headers.sessionid = sessionId;
      config.headers.callid = config.metadata?.correlationId || config.headers['X-Correlation-ID'];

      return config;
    })
  );
};

/**
 * Attaches request and response interceptors for API lifecycle logging.
 * @param axiosInstance - The Axios instance to instrument
 * @param label - Label to distinguish backends in logs (e.g. "node", "dotnet")
 */
export const configureAxiosLogging = (axiosInstance: AxiosInstance, label: string): void => {
  // Request interceptor: generate correlation ID, log api_request_started
  axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
    const correlationId = createCorrelationId();
    config.metadata = {
      correlationId,
      startTime: Date.now()
    };

    logEvent({
      event: 'api_request_started',
      level: 'Debug',
      url: config.url,
      method: config.method?.toUpperCase(),
      correlationId,
      baseUrl: label
    });

    return config;
  });

  // Response interceptor: log api_request_completed
  axiosInstance.interceptors.response.use(
    (response) => {
      const { metadata } = response.config;
      const durationMs = metadata ? Date.now() - metadata.startTime : undefined;

      logEvent({
        event: 'api_request_completed',
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        correlationId: metadata?.correlationId,
        statusCode: response.status,
        durationMs,
        baseUrl: label
      });

      return response;
    },
    (error) => {
      const config = error.config as AxiosRequestConfig | undefined;
      const metadata = config?.metadata;
      const durationMs = metadata ? Date.now() - metadata.startTime : undefined;

      logEvent({
        event: 'api_request_failed',
        level: 'Error',
        url: config?.url,
        method: config?.method?.toUpperCase(),
        correlationId: metadata?.correlationId,
        statusCode: error.response?.status,
        durationMs,
        errorType: error.name || 'Error',
        errorMessage: error.message,
        baseUrl: label
      });

      return Promise.reject(error);
    }
  );
};

// Register logging interceptors at module init (before auth interceptors)
configureAxiosLogging(axios, 'node');
configureAxiosLogging(dotNetAxios, 'dotnet');

dotNetAxios.defaults.headers.common['Content-Type'] = 'application/json';
